import { InjectionKey } from 'vue'
import { ActionTree, createStore, GetterTree, MutationTree, Store, StoreOptions, useStore } from 'vuex'
import { EDeviceTypeName } from '../types'
import { Device, DeviceHms, DeviceOsd, DeviceStatus, DockOsd, GatewayOsd, OSDVisible } from '../types/device'
import { getElementGroupsReq } from '/@/api/layer'
import { LayerType } from '/@/types/mapLayer'
import { WaylineFile } from '/@/types/wayline'
import { DevicesCmdExecuteInfo } from '/@/types/device-cmd'
import { FlightAreaStatus } from '../api/flight-area'

const initStateFunc = () => ({
  Layers: [
    {
      name: 'default',
      id: '',
      is_distributed: true,
      elements: [],
      is_check: false,
      is_select: false,
      type: 1
    },
    {
      name: 'share',
      id: '',
      is_distributed: true,
      elements: [],
      is_check: false,
      is_select: false,
      type: 2
    }
  ],
  layerBaseInfo: {} as {
    [key:string]:string
  },
  drawVisible: false,
  livestreamOthersVisible: false,
  livestreamAgoraVisible: false,
  livestreamSwapped: false,
  livestreamLayout: 'split' as 'split' | 'video-bg' | 'grid',
  splitDirection: 'h' as 'h' | 'v',
  splitRatio: 0.5 as number,
  activeStreams: {} as {
    [sn: string]: {
      sn: string
      name: string
      url: string
      type: number
      videoId: string
      quality: number
      droneSn: string
      startedByMe?: boolean
    }
  },
  coverMap: {} as {
    [key: string]: any[]
  },
  wsEvent: {
    mapElementCreat: {},
    mapElementUpdate: {},
    mapElementDelete: {}
  },
  deviceStatusEvent: {
    deviceOnline: {} as DeviceStatus,
    deviceOffline: {}
  },
  markerInfo: {
    coverMap: {} as {
      [sn: string]: any
    },
    pathMap: {} as {
      [sn: string]: any[]
    }
  },
  deviceState: {
    // remote controller, dock
    gatewayInfo: {} as {
      [sn: string]: GatewayOsd
    },
    // drone
    deviceInfo: {} as {
      [sn: string]: DeviceOsd
    },
    dockInfo: {} as {
      [sn: string]: DockOsd
    },
    currentSn: '',
    currentType: -1
  },
  osdVisible: { // osd 显示设备相关信息
    sn: '',
    callsign: '',
    model: '',
    visible: false,
    gateway_sn: '',
    is_dock: false,
    payloads: null
  } as OSDVisible,
  waylineInfo: {

  } as WaylineFile,
  dockInfo: {

  } as Device,
  hmsInfo: {} as {
    [sn: string]: DeviceHms[]
  },
  // 机场指令执行状态信息
  devicesCmdExecuteInfo: {
  } as DevicesCmdExecuteInfo,
  mqttState: null as any, // mqtt 实例
  clientId: '', // mqtt 连接 唯一客户端id
  // Mapping drone SN → gateway SN (populé depuis les events DeviceOnline)
  droneToGatewayMap: {} as { [droneSn: string]: string },
  selectedMapElementId: '' as string,
  layerRedrawTrigger: 0,
  activeGroupId: '' as string,
  geoidOffset: null as number | null, // WGS84 − MSL (≈ +47 m in France), calibrated once from drone OSD
  userLocations: {} as { [userId: string]: { userId: string; username: string; userColor?: string; userAvatar?: string | null; lat: number; lng: number; timestamp: number } },
  // Maps drone SN → display callsign (populated from device topo + deviceOnline events)
  snToCallsign: {} as Record<string, string>,
  // RTH home point received from RC via WebSocket (rc_home_point biz code)
  rthHomePoint: null as { sn: string; lat: number; lng: number } | null,
  // Weather drone status — null = not yet loaded
  weatherStatus: null as 'go' | 'caution' | 'nogo' | null,
})

export type RootStateType = ReturnType<typeof initStateFunc>

const getters: GetterTree<RootStateType, RootStateType> = {
}
const mutations: MutationTree<RootStateType> = {
  RESET_WORKSPACE_STATE (state) {
    const fresh = initStateFunc()
    state.Layers = fresh.Layers
    state.layerBaseInfo = fresh.layerBaseInfo
    state.coverMap = fresh.coverMap
    state.wsEvent = fresh.wsEvent
    state.deviceStatusEvent = fresh.deviceStatusEvent
    // Mutate in-place so module-level references to the sub-objects stay valid
    Object.keys(state.markerInfo.coverMap).forEach(k => delete state.markerInfo.coverMap[k])
    Object.keys(state.markerInfo.pathMap).forEach(k => delete state.markerInfo.pathMap[k])
    state.deviceState = fresh.deviceState
    state.osdVisible = fresh.osdVisible
    state.hmsInfo = fresh.hmsInfo
    state.devicesCmdExecuteInfo = fresh.devicesCmdExecuteInfo
    state.droneToGatewayMap = fresh.droneToGatewayMap
    state.activeStreams = fresh.activeStreams
    state.userLocations = fresh.userLocations
    state.activeGroupId = fresh.activeGroupId
    state.selectedMapElementId = fresh.selectedMapElementId
    state.snToCallsign = fresh.snToCallsign
  },
  SET_LAYER_INFO (state, info) {
    state.Layers = info
  },
  SET_DEVICE_INFO (state, info) {
    state.deviceState.deviceInfo[info.sn] = info.host
    state.deviceState.currentSn = info.sn
    state.deviceState.currentType = EDeviceTypeName.Aircraft
  },
  SET_GATEWAY_INFO (state, info) {
    state.deviceState.gatewayInfo[info.sn] = info.host
    state.deviceState.currentSn = info.sn
    state.deviceState.currentType = EDeviceTypeName.Gateway
  },
  SET_DOCK_INFO (state, info) {
    if (Object.keys(info.host).length === 0) {
      return
    }
    if (!state.deviceState.dockInfo[info.sn]) {
      state.deviceState.dockInfo[info.sn] = { } as DockOsd
    }
    state.deviceState.currentSn = info.sn
    state.deviceState.currentType = EDeviceTypeName.Dock
    const dock = state.deviceState.dockInfo[info.sn]
    if (info.host.mode_code !== undefined) {
      dock.basic_osd = info.host
      return
    }
    if (info.host.wireless_link) {
      dock.link_osd = info.host
      return
    }
    if (info.host.job_number !== undefined) {
      dock.work_osd = info.host
    }
  },
  SET_DRAW_VISIBLE_INFO (state, bool) {
    state.drawVisible = bool
  },
  SET_LIVESTREAM_OTHERS_VISIBLE (state, bool) {
    state.livestreamOthersVisible = bool
  },
  SET_LIVESTREAM_AGORA_VISIBLE (state, bool) {
    state.livestreamAgoraVisible = bool
  },
  SET_LIVESTREAM_SWAPPED (state, bool) {
    state.livestreamSwapped = bool
  },
  SET_LIVESTREAM_LAYOUT (state, layout: 'split' | 'video-bg' | 'grid') {
    state.livestreamLayout = layout
  },
  SET_SPLIT_DIRECTION (state, dir: 'h' | 'v') {
    state.splitDirection = dir
  },
  SET_SPLIT_RATIO (state, ratio: number) {
    state.splitRatio = ratio
  },
  SET_STREAM_ACTIVE (state, stream: { sn: string, name: string, url: string, type: number, videoId: string, quality: number, droneSn: string, startedByMe?: boolean }) {
    // Enforce one stream per drone SN — remove any existing entry for the same drone before adding
    const streams = { ...state.activeStreams }
    Object.keys(streams).forEach(k => {
      if (streams[k].sn === stream.sn && k !== (stream.videoId || stream.sn)) delete streams[k]
    })
    streams[stream.videoId || stream.sn] = stream
    state.activeStreams = streams
  },
  SET_STREAM_STOP (state, videoId: string) {
    const streams = { ...state.activeStreams }
    delete streams[videoId]
    state.activeStreams = streams
  },
  SET_STREAMS_STOP_BY_SN (state, sn: string) {
    const streams = { ...state.activeStreams }
    Object.keys(streams).forEach(k => { if (streams[k].sn === sn || streams[k].droneSn === sn) delete streams[k] })
    state.activeStreams = streams
  },
  SET_MAP_ELEMENT_CREATE (state, info) {
    state.wsEvent.mapElementCreat = info
  },
  SET_MAP_ELEMENT_UPDATE (state, info) {
    state.wsEvent.mapElementUpdate = info
  },
  SET_MAP_ELEMENT_DELETE (state, info) {
    state.wsEvent.mapElementDelete = info
  },
  SET_DEVICE_ONLINE (state, info) {
    state.deviceStatusEvent.deviceOnline = info
    // Mémorise la correspondance drone → gateway pour le lookup OSD
    if (info.sn && info.gateway_sn) {
      state.droneToGatewayMap[info.sn] = info.gateway_sn
    }
  },
  SET_DEVICE_OFFLINE (state, info) {
    state.deviceStatusEvent.deviceOffline = info
    delete state.deviceState.gatewayInfo[info.sn]
    delete state.deviceState.deviceInfo[info.sn]
    delete state.deviceState.dockInfo[info.sn]
    delete state.hmsInfo[info.sn]
    // Drone déconnecté : on coupe AUSSI son flux actif. Sinon le lecteur reconnecte en
    // boucle un flux mort (RC coupée), ce qui parasite le démarrage du live suivant.
    const streams = { ...state.activeStreams }
    let changed = false
    Object.keys(streams).forEach(k => {
      if (streams[k].sn === info.sn || streams[k].droneSn === info.sn) { delete streams[k]; changed = true }
    })
    if (changed) state.activeStreams = streams
    // delete state.markerInfo.coverMap[info.sn]
    // delete state.markerInfo.pathMap[info.sn]
  },
  SET_OSD_VISIBLE_INFO (state, info) {
    state.osdVisible = info
  },
  SET_SELECT_WAYLINE_INFO (state, info) {
    state.waylineInfo = info
  },
  SET_SELECT_DOCK_INFO (state, info) {
    state.dockInfo = info
  },
  SET_DEVICE_HMS_INFO (state, info) {
    const hmsList: Array<DeviceHms> = state.hmsInfo[info.sn]
    state.hmsInfo[info.sn] = info.host.concat(hmsList ?? [])
  },
  SET_DEVICES_CMD_EXECUTE_INFO (state, info) { // 保存设备指令ws消息推送
    if (!info.sn) {
      return
    }
    if (state.devicesCmdExecuteInfo[info.sn]) {
      const index = state.devicesCmdExecuteInfo[info.sn].findIndex(cmdExecuteInfo => cmdExecuteInfo.biz_code === info.biz_code)
      if (index >= 0) {
        // 丢弃前面的消息
        if (state.devicesCmdExecuteInfo[info.sn][index].timestamp > info.timestamp) {
          return
        }
        state.devicesCmdExecuteInfo[info.sn][index] = info
      } else {
        state.devicesCmdExecuteInfo[info.sn].push(info)
      }
    } else {
      state.devicesCmdExecuteInfo[info.sn] = [info]
    }
  },
  SET_MQTT_STATE (state, mqttState) {
    state.mqttState = mqttState
  },
  SET_CLIENT_ID (state, clientId) {
    state.clientId = clientId
  },
  SET_SELECTED_MAP_ELEMENT (state, id: string) {
    state.selectedMapElementId = id
  },
  TRIGGER_LAYER_REDRAW (state) {
    state.layerRedrawTrigger++
  },
  SET_GEOID_OFFSET (state, offset: number) {
    state.geoidOffset = offset
  },
  SET_ACTIVE_GROUP (state, id: string) {
    state.activeGroupId = id
  },
  SET_SN_TO_CALLSIGN (state, { sn, callsign }: { sn: string; callsign: string }) {
    if (sn && callsign) state.snToCallsign[sn] = callsign
  },
  SET_RTH_HOME_POINT (state, hp: { sn: string; lat: number; lng: number } | null) {
    state.rthHomePoint = hp
  },
  SET_WEATHER_STATUS (state, status: 'go' | 'caution' | 'nogo' | null) {
    state.weatherStatus = status
  },
  SET_USER_LOCATION (state, loc: { userId: string; username: string; userColor?: string; userAvatar?: string | null; lat: number; lng: number; timestamp: number; removed?: boolean }) {
    if (loc.removed) {
      const locs = { ...state.userLocations }
      delete locs[loc.userId]
      state.userLocations = locs
    } else {
      state.userLocations = { ...state.userLocations, [loc.userId]: loc }
    }
  },
}

const actions: ActionTree<RootStateType, RootStateType> = {
  async getAllElement ({ commit }) {
    const result = await getElementGroupsReq({
      groupId: '',
      isDistributed: true
    })
    if (result?.data) {
      commit('SET_LAYER_INFO', result.data)
    }
  },
  updateElement ({ state }, content: {type: 'is_check' | 'is_select', id: string, bool:boolean}) {
    const key = content.id.replaceAll('resource__', '')
    const type = content.type
    const layers = state.Layers
    const layer = layers.find(item => item.id === key)
    if (layer) {
      layer[type] = content.bool
    }
  },
  setLayerInfo ({ state }, layers) {
    // const layers = state.Layers
    const obj:{
      [key:string]:string
    } = {}
    layers.forEach(layer => {
      if (layer.type === LayerType.Default) {
        obj.default = layer.id
      } else {
        if (layer.type === LayerType.Share) {
          obj.share = layer.id
        }
      }
    })
    state.layerBaseInfo = obj
    console.log('state.layerBaseInfo', state.layerBaseInfo)
  },
  getLayerInfo ({ state }, id:string) {
    return state.layerBaseInfo[id]
  }
}

const storeOptions: StoreOptions<RootStateType> = {
  state: initStateFunc,
  getters,
  mutations,
  actions
}

const rootStore = createStore(storeOptions)

export default rootStore

export const storeKey: InjectionKey<Store<RootStateType>> = Symbol('')

type AllStateStoreTypes = RootStateType & {
  // moduleName: moduleType
}

export function useMyStore<T = AllStateStoreTypes> () {
  return useStore<T>(storeKey)
}
